import {Button, Flex, Input, Modal, Select, Switch, Table, TableColumnsType} from 'antd'
import {providersConfig} from "../components/api";
import {useEffect, useState} from "react";
import {ErrorView} from "../components/show-error";
import StringSelector from "../components/string-selector";
import {useSearchParams} from "react-router";
import {
  SettingType,
  SettingTypeSchema,
  SettingTable,
  SettingTableSchema,
  MergeModelResponse_Delta, Action, ProviderModel,
  Result,
} from '../api/ga/config_manager/providersconfig/providers_config_pb'
import {DataEntryParent} from "../components/data-entry-parent";
import ResultModal from "../components/result-modal";

const UniversalProviderName = "universal"

export default function ProviderModelView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [modal, contextHolder] = Modal.useModal()

  const [rows, setRows] = useState([] as DataType[])
  const [masterRows, setMasterRows] = useState(new Map<string, DataType>())
  const [branches, setBranches] = useState([] as string[])
  const [branch, setBranch] = useState('')
  const [providers, setProviders] = useState([] as string[])
  const [provider, setProvider] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalIsEdit, setModalIsEdit] = useState(true)
  const [modalRow, setModalRow] = useState<DataType | null>(null)
  const [modalRowEdit, setModalRowEdit] = useState<DataType | null>(null)
  const [newBranchOpen, setNewBranchOpen] = useState(false)
  const [newBranchName, setNewBranchName] = useState('')

  const [copyProviderOpen, setCopyProviderOpen] = useState(false)
  const [copyProviderRow, setCopyProviderRow] = useState<DataType | null>(null)
  const [copyProviderName, setCopyProviderName] = useState('')

  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeLoading, setMergeLoading] = useState(false)
  const [mergeDeltas, setMergeDeltas] = useState([] as MergeModelResponse_Delta[])
  const [mergeParams, setMergeParams] = useState({fromBranch: '', toBranch: ''})

  const [importOpen, setImportOpen] = useState(false)
  const [importRes, setImportRes] = useState<Result | undefined>(undefined)
  const [importAskOpen, setImportAskOpen] = useState(false)
  const [importFromGitBranch, setImportFromGitBranch] = useState('master')
  const [importLoading, setImportLoading] = useState(false)

  const [exportOpen, setExportOpen] = useState(false)
  const [exportRes, setExportRes] = useState<Result | undefined>(undefined)
  const [exportLoading, setExportLoading] = useState(false)

  interface DataType {
    key: string
    branch: string
    provider: string
    table: SettingTable
    setting: string
    settingType: SettingType
    comment: string
    required: boolean
    action: Action
    is_provider_layer: boolean
    is_table_layer: boolean
    children?: DataType[]
  }

  const loadBranches = () => {
    providersConfig.modelsBranches({})
      .then((resp) => {
        setBranches(resp.branches.sort())
      })
      .catch((e) => {
        ErrorView(modal, 'modelsBranches error', e)
      })
  }

  const loadModels = () => {
    providersConfig.models({branch: branch})
      .then((resp) => {
        const providersSet = new Set<string>()
        // provider -> table -> setting (incl. nested via dots) -> node
        const treeMap = new Map<string, Map<SettingTable, Map<string, DataType>>>()

        function getOrCreate(model: ProviderModel): DataType {
          let tableMap = treeMap.get(model.provider)
          if (!tableMap) {
            tableMap = new Map<SettingTable, Map<string, DataType>>()
            treeMap.set(model.provider, tableMap)
          }
          let settingMap = tableMap.get(model.table)
          if (!settingMap) {
            settingMap = new Map<string, DataType>()
            tableMap.set(model.table, settingMap)
          }
          let node = settingMap.get(model.setting)
          if (!node) {
            node = {
              key: `${model.provider}:${model.table}:${model.setting}`,
              branch: model.branch,
              provider: model.provider,
              table: model.table,
              setting: model.setting,
              settingType: model.settingType,
              comment: model.comment,
              required: model.required,
              action: model.action,
              is_provider_layer: false,
              is_table_layer: false,
              children: undefined,
            }
            settingMap.set(model.setting, node)
            const lastDot = model.setting.lastIndexOf('.')
            if (lastDot !== -1) {
              const parent = getOrCreate({
                ...model,
                setting: model.setting.substring(0, lastDot),
                settingType: SettingType.UNKNOWN,
                comment: '',
                required: false,
                action: Action.NO_OR_UPSET,
              })
              parent.children = [...(parent.children ?? []), node]
            }
          }
          return node
        }

        const masterRowsMap = new Map<string, DataType>()
        resp.models?.forEach((model) => {
          providersSet.add(model.provider)
          const node = getOrCreate(model)
          node.branch = model.branch
          node.settingType = model.settingType
          node.comment = model.comment
          node.required = model.required
          node.action = model.action
          if (model.branch === 'master') {
            masterRowsMap.set(`${model.provider}:${model.table}:${model.setting}`, node)
          }
        })

        const sortBySetting = (a: DataType, b: DataType) => a.setting.localeCompare(b.setting)

        const roots: DataType[] = []
        treeMap.forEach((tableMap, prov) => {
          const tableLayers: DataType[] = []
          let provBranch = 'master'
          tableMap.forEach((settingMap, table) => {
            const settingRoots: DataType[] = []
            let tableBranch = 'master'
            settingMap.forEach((node) => {
              if (node.children) node.children.sort(sortBySetting)
              if (node.setting.lastIndexOf('.') === -1) settingRoots.push(node)
              if (node.branch !== 'master') tableBranch = node.branch
            })
            settingRoots.sort(sortBySetting)
            if (tableBranch !== 'master') provBranch = tableBranch
            tableLayers.push({
              key: `${prov}:${table}`,
              branch: tableBranch,
              provider: prov,
              table,
              setting: '',
              settingType: SettingType.UNKNOWN,
              comment: '',
              required: false,
              action: Action.NO_OR_UPSET,
              is_provider_layer: false,
              is_table_layer: true,
              children: settingRoots,
            })
          })
          tableLayers.sort((a, b) => a.table - b.table)
          roots.push({
            key: prov,
            branch: provBranch,
            provider: prov,
            table: 0 as SettingTable,
            setting: '',
            settingType: SettingType.UNKNOWN,
            comment: '',
            required: false,
            action: Action.NO_OR_UPSET,
            is_provider_layer: true,
            is_table_layer: false,
            children: tableLayers,
          })
        })
        roots.sort((a, b) => {
          if (a.provider === UniversalProviderName) return -1
          if (b.provider === UniversalProviderName) return 1
          return a.provider.localeCompare(b.provider)
        })

        setRows(roots)
        setProviders(Array.from(providersSet).sort((a, b) => {
          if (a === UniversalProviderName) return -1
          if (b === UniversalProviderName) return 1
          return a.localeCompare(b)
        }))
        setMasterRows(masterRowsMap)
      })
      .catch((e) => {
        ErrorView(modal, 'models error', e)
      })
  }

  useEffect(() => {
    setBranch(searchParams.get('branch') ?? 'master')
    setProvider(searchParams.get('provider') ?? '')
    loadBranches()
  }, [])

  useEffect(() => {
    if (!branch) return

    setSearchParams((par) => {
      par.set('branch', branch)
      if (provider) par.set('provider', provider); else par.delete('provider')
      return par
    })

    loadModels()
  }, [branch])

  useEffect(() => {
    setSearchParams((par) => {
      if (provider) par.set('provider', provider); else par.delete('provider')
      return par
    })
  }, [provider])

  const selectOptionsSettingTypeSchema: any[] = []
  SettingTypeSchema.values.forEach((val) => {
    if (val.number == 0) return

    selectOptionsSettingTypeSchema.push({
      value: val.number,
      label: val.name.toLowerCase(),
    })
  })

  const selectOptionsSettingTableSchema: any[] = []
  SettingTableSchema.values.forEach((val) => {
    if (val.number == 0 || val.number == 4) return

    selectOptionsSettingTableSchema.push({
      value: val.number,
      label: val.name.replace(/^TABLE_/, '').toLowerCase(),
    })
  })

  const tableLabel = (t: SettingTable) =>
    SettingTableSchema.values.find((v) => v.number === t)?.name.replace(/^TABLE_/, '').toLowerCase() ?? String(t)

  const columns: TableColumnsType<DataType> = [
    // {
    //   title: 'provider',
    //   dataIndex: 'provider',
    //   key: 'provider',
    //   width: '20vw',
    // },
    {
      title: 'provider / table / setting',
      dataIndex: 'setting',
      key: 'setting',
      width: '35vw',
      render: (_, {provider, is_provider_layer, table, is_table_layer, setting}) => {
        if (is_provider_layer) return provider
        if (is_table_layer) return tableLabel(table)
        const lastDot = setting.lastIndexOf('.')
        return lastDot !== -1 ? setting.substring(lastDot + 1) : setting
      },
    },
    // {
    //   title: 'setting',
    //   dataIndex: 'setting',
    //   key: 'setting',
    //   width: '30vw',
    //   render: (_, {setting}) => {
    //     const lastDot = setting.lastIndexOf('.')
    //     return lastDot !== -1 ? setting.substring(lastDot + 1) : setting
    //   },
    // },
    // {
    //   title: 'branch',
    //   dataIndex: 'branch',
    //   key: 'branch',
    //   width: '14vw',
    // },
    {
      title: 'type',
      dataIndex: 'settingType',
      key: 'settingType',
      width: '8vw',
      render: (_, {settingType, children}) => children?.length ? ''
        : SettingTypeSchema.values.find((v) => v.number === settingType)?.name.toLowerCase() ?? settingType,
    },
    {
      title: 'required',
      dataIndex: 'required',
      key: 'required',
      width: '7vw',
      render: (_, {required, is_provider_layer, is_table_layer, children}) =>
        is_provider_layer || is_table_layer || children?.length ? '' : (required ? '✓' : ''),
    },
    {
      title: 'comment',
      dataIndex: 'comment',
      key: 'comment',
      width: '20vw',
      render: (_, {comment, is_provider_layer, is_table_layer, children}) =>
        is_provider_layer || is_table_layer || children?.length ? '' : comment,
    },
    {
      title: 'actions',
      key: 'actions',
      width: '12vw',
      render: (_, row) => {
        if (branch === 'master') return ""
        if (row.is_provider_layer) return (
          <Flex gap={4}>
            <Button size="small" onClick={() => {
              setCopyProviderRow(row);
              setCopyProviderName('');
              setCopyProviderOpen(true)
            }}>copy</Button>
          </Flex>
        )
        if (row.is_table_layer) return ""
        return (
          <Flex gap={4}>
            <Button size="small" onClick={() => {
              setModalIsEdit(true);
              setModalRow(row);
              setModalRowEdit({...row});
              setModalOpen(true)
            }}>edit</Button>
            <Button size="small" onClick={() => {
              setModalIsEdit(false);
              setModalRow(row);
              setModalRowEdit({...row});
              setModalOpen(true)
            }}>copy</Button>
            <Button size="small" danger onClick={() => {
              modal.confirm({
                title: 'Delete setting',
                content: `Delete "${row.setting}" (${row.provider}/${tableLabel(row.table)})?`,
                onOk: () => {
                  if (row.branch === branch) {
                    providersConfig.deleteModel({
                      branch: branch,
                      provider: row.provider,
                      table: row.table,
                      setting: [row.setting],
                    })
                      .then(() => loadModels())
                      .catch((e: any) => {
                        ErrorView(modal, 'deleteModel error', e)
                      })
                  } else {
                    providersConfig.upsertModel({
                      models: [{
                        branch: branch,
                        provider: row.provider,
                        table: row.table,
                        setting: row.setting,
                        settingType: row.settingType,
                        comment: row.comment,
                        required: row.required,
                        action: Action.DELETE,
                      }],
                    }).then(() => {
                      setModalOpen(false);
                      loadModels()
                    })
                      .catch((e) => {
                        ErrorView(modal, 'upsertModel error', e)
                      })
                  }
                },
              })
            }}>delete</Button>
          </Flex>
        )
      },
    },
  ]

  return (
    <>
      <div style={{maxWidth: 1400, margin: '0 auto', padding: '24px 24px 40px', textAlign: 'left'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0}}>
        <h1 style={{fontSize: 32, fontWeight: 900, color: 'var(--type-primary)', margin: 0}}>
          Provider Model
        </h1>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 16, alignItems: 'start'}}>
        <DataEntryParent text="branch">
          <StringSelector
            items={branches}
            value={[branch]}
            onChange={(value: string[]) => {
              const val = value ? value[0] : ''
              setBranch(val)
            }}
          ></StringSelector>
        </DataEntryParent>
        <DataEntryParent text="provider">
          <StringSelector
            items={['', ...providers]}
            value={[provider]}
            onChange={(value: string[]) => {
              const val = value ? value[0] : ''
              setProvider(val)
            }}
          ></StringSelector>
        </DataEntryParent>
        <DataEntryParent text="New">
          <Button onClick={() => {
            setNewBranchName('');
            setNewBranchOpen(true);
          }}>New Branch from current</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>
            <DataEntryParent text="Delete">
              <Button danger onClick={() => {
                modal.confirm({
                  title: 'Delete branch',
                  content: `Delete branch "${branch}" and all its settings?`,
                  onOk: () => {
                    providersConfig.deleteModel({
                      branch: branch,
                    })
                      .then(() => {
                        setBranch('master');
                        loadBranches()
                      })
                      .catch((e: any) => {
                        ErrorView(modal, 'deleteModel error', e)
                      })
                  },
                })
              }}>Delete branch</Button>
            </DataEntryParent>
            <DataEntryParent text="Import">
              <Button onClick={() => {
                setImportFromGitBranch('master');
                setImportAskOpen(true)
              }}>Import from git</Button>
            </DataEntryParent>
            <DataEntryParent text="Merge">
              <Button danger loading={mergeLoading} onClick={() => {
                const params = {fromBranch: branch, toBranch: 'master'}
                providersConfig.mergeModelCompare(params)
                  .then((resp) => {
                    setMergeParams(params);
                    setMergeDeltas(resp.deltas ?? []);
                    setMergeOpen(true)
                  })
                  .catch((e: any) => {
                    if (String(e?.message ?? e).includes('no data for merge')) {
                      modal.warning({title: 'Merge', content: 'No data for merge'})
                    } else {
                      ErrorView(modal, 'mergeModelCompare error', e)
                    }
                  })
              }}>Merge branch</Button>
            </DataEntryParent>
          </>
        )}
        <DataEntryParent text="Export">
          <Button loading={exportLoading} onClick={() => {
            setExportLoading(true);
            providersConfig.exportModels({ fromBranch: branch })
              .then((resp) => {
                setExportRes(resp.res)
                setExportOpen(true)
                if (resp.gitBranch || resp.gitMrLink) {
                  modal.info({
                    title: 'Export pushed to git',
                    content: (
                      <p>
                        Branch <b>{resp.gitBranch}</b> created.{' '}
                        {resp.gitMrLink && <a href={resp.gitMrLink} target="_blank" rel="noreferrer">Open merge request</a>}
                      </p>
                    ),
                  })
                }
              })
              .catch((e: any) => { ErrorView(modal, 'exportModels error', e) })
              .finally(() => setExportLoading(false))
          }}>Export to git</Button>
        </DataEntryParent>
      </div>
      <div style={{background: 'var(--surface-white)', borderRadius: 'var(--radius-basic)', border: '1px solid var(--border)', overflow: 'hidden'}}>
      <Table
        dataSource={rows
          // .filter((r) => !branch || r.branch === branch)
          .filter((r) => !provider || r.provider === provider)
      }
        columns={columns}
        size={'small'}
        pagination={{defaultPageSize: 100}}
        rowClassName={(row) => row.branch === 'master'? '' :
            row.action === Action.DELETE ? 'merge-removed' :
              (row.is_table_layer || row.is_provider_layer) ? 'merge-changed' :
                (row.key in masterRows ? 'merge-changed' : 'merge-added')
        }
      />
      </div>
      </div>
      <Modal
        title={modalIsEdit ? 'Edit' : 'Copy'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          if (!modalRowEdit || !modalRow) return

          const doUpsert = () => {
            providersConfig.upsertModel({
              models: [{
                branch: branch,
                provider: modalRowEdit.provider,
                table: modalRowEdit.table,
                setting: modalRowEdit.setting,
                settingType: modalRowEdit.settingType,
                comment: modalRowEdit.comment,
                required: modalRowEdit.required,
              }],
            }).then(() => {
              setModalOpen(false);
              loadModels()
            })
              .catch((e) => {
                ErrorView(modal, 'upsertModel error', e)
              })
          }

          const sameKey = modalRowEdit.setting === modalRow.setting
            && modalRowEdit.provider === modalRow.provider
            && modalRowEdit.table === modalRow.table

          if (!modalIsEdit && sameKey) {
            modal.warning({
              title: 'Setting not changed',
              content: 'Please change setting/provider/table before copying.',
            })
            return
          }

          if (modalIsEdit && !sameKey) {
            modal.confirm({
              title: 'Setting key changed',
              content: `"${modalRow.provider}/${tableLabel(modalRow.table)}/${modalRow.setting}" → "${modalRowEdit.provider}/${tableLabel(modalRowEdit.table)}/${modalRowEdit.setting}". Old setting will be deleted. Continue?`,
              onOk: () => {
                providersConfig.deleteModel({
                  branch: branch,
                  provider: modalRow.provider,
                  table: modalRow.table,
                  setting: [modalRow.setting],
                })
                  .then(doUpsert)
                  .catch((e) => {
                    ErrorView(modal, 'deleteModel error', e)
                  })
              },
            })
          } else {
            doUpsert()
          }
        }}
      >
        {modalRowEdit && (
          <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
            <span style={{fontWeight: 500}}>branch</span>
            <span>{modalRowEdit.branch}</span>

            <span style={{fontWeight: 500}}>provider</span>
            <Input
              value={modalRowEdit.provider}
              onChange={(e) => setModalRowEdit({...modalRowEdit, provider: e.target.value})}
            />

            <span style={{fontWeight: 500}}>table</span>
            <Select
              value={modalRowEdit.table}
              options={selectOptionsSettingTableSchema}
              onChange={(val) => setModalRowEdit({...modalRowEdit, table: val})}
              style={{width: '100%'}}
            />

            <span style={{fontWeight: 500}}>setting</span>
            <Input
              value={modalRowEdit.setting}
              onChange={(e) => setModalRowEdit({...modalRowEdit, setting: e.target.value})}
            />

            <span style={{fontWeight: 500}}>type</span>
            <Select
              value={modalRowEdit.settingType}
              options={selectOptionsSettingTypeSchema}
              onChange={(val) => setModalRowEdit({...modalRowEdit, settingType: val})}
              style={{width: '100%'}}
            />

            <span style={{fontWeight: 500}}>required</span>
            <Switch
              checked={modalRowEdit.required}
              onChange={(val) => setModalRowEdit({...modalRowEdit, required: val})}
            />

            <span style={{fontWeight: 500}}>comment</span>
            <Input.TextArea
              value={modalRowEdit.comment}
              autoSize={{minRows: 1, maxRows: 4}}
              onChange={(e) => setModalRowEdit({...modalRowEdit, comment: e.target.value})}
            />
          </div>
        )}
      </Modal>
      <Modal
        title="New Branch from current"
        open={newBranchOpen}
        onCancel={() => setNewBranchOpen(false)}
        onOk={() => {
          const newBranch = newBranchName.trim()

          if (!newBranch) {
            modal.warning({title: 'Branch name is empty'})
            return
          }
          if (branches.includes(newBranch)) {
            modal.warning({title: 'Branch already exists', content: `"${newBranch}" already exists.`})
            return
          }

          providersConfig.copyModelsBranch({fromBranch: branch, toBranch: newBranch})
            .then(() => {
              setNewBranchOpen(false);
              setBranch(newBranch);
              loadBranches();
              loadModels();
            })
            .catch((e: any) => {
              ErrorView(modal, 'copyModelsBranch error', e)
            })
        }}
      >
        <Input
          placeholder="New branch name"
          value={newBranchName}
          onChange={(e) => setNewBranchName(e.target.value)}
        />
      </Modal>
      <Modal
        title={copyProviderRow ? `Copy provider "${copyProviderRow.provider}"` : 'Copy provider'}
        open={copyProviderOpen}
        onCancel={() => setCopyProviderOpen(false)}
        onOk={() => {
          if (!copyProviderRow) return

          const newProvider = copyProviderName.trim()

          if (!newProvider) {
            modal.warning({title: 'Provider name is empty'})
            return
          }
          if (newProvider === copyProviderRow.provider) {
            modal.warning({title: 'Same provider', content: 'Please enter a different provider name.'})
            return
          }

          // Collect all real settings nested under the provider row (skip layer and synthetic grouping nodes).
          const models: {
            branch: string
            provider: string
            table: SettingTable
            setting: string
            settingType: SettingType
            comment: string
            required: boolean
          }[] = []
          const collect = (node: DataType) => {
            if (!node.is_provider_layer && !node.is_table_layer && node.settingType !== SettingType.UNKNOWN) {
              models.push({
                branch: branch,
                provider: newProvider,
                table: node.table,
                setting: node.setting,
                settingType: node.settingType,
                comment: node.comment,
                required: node.required,
              })
            }
            node.children?.forEach(collect)
          }
          collect(copyProviderRow)

          if (!models.length) {
            modal.warning({title: 'Nothing to copy', content: 'No settings found for this provider.'})
            return
          }

          providersConfig.upsertModel({models})
            .then(() => {
              setCopyProviderOpen(false);
              setProvider(newProvider);
              loadModels()
            })
            .catch((e) => {
              ErrorView(modal, 'upsertModel error', e)
            })
        }}
        okText="Copy"
      >
        <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
          <span style={{fontWeight: 500}}>new provider</span>
          <Input
            placeholder="New provider name"
            value={copyProviderName}
            onChange={(e) => setCopyProviderName(e.target.value)}
          />
        </div>
      </Modal>
      <Modal
        title="Merge branch comparison"
        open={mergeOpen}
        onCancel={() => setMergeOpen(false)}
        onOk={() => {
          modal.confirm({
            title: 'Merge branch',
            content: `Merge "${mergeParams.fromBranch}" into "${mergeParams.toBranch}"?`,
            onOk: () => {
              setMergeOpen(false);
              setMergeLoading(true);
              providersConfig.mergeModelDo(mergeParams)
                .then(() => {
                  setBranch('master');
                  loadBranches();
                  loadModels();
                })
                .catch((e: any) => {
                  ErrorView(modal, 'mergeModelDo error', e)
                })
                .finally(() => setMergeLoading(false))
            },
          })
        }}
        okText="Merge"
        width="90vw"
      >
        <Table
          size="small"
          pagination={{pageSize: 25}}
          rowKey={(_, i) => String(i)}
          rowClassName={(d) => !d.valueTo ? 'merge-added' : !d.valueFrom ? 'merge-removed' : 'merge-changed'}
          dataSource={mergeDeltas}
          columns={[
            {title: 'provider', dataIndex: 'provider', key: 'provider'},
            {title: 'table', dataIndex: 'table', key: 'table', render: (v) => tableLabel(v)},
            {title: 'setting', dataIndex: 'setting', key: 'setting'},
            {
              title: 'before',
              dataIndex: 'valueTo',
              key: 'valueTo',
              render: (v) => v || <span style={{color: '#999'}}>&lt;default&gt;</span>
            },
            {
              title: 'new',
              dataIndex: 'valueFrom',
              key: 'valueFrom',
              render: (v) => v || <span style={{color: '#999'}}>&lt;default&gt;</span>
            },
          ]}
        />
      </Modal>
      <Modal
        title="Import from git"
        open={importAskOpen}
        onCancel={() => setImportAskOpen(false)}
        confirmLoading={importLoading}
        cancelButtonProps={{disabled: importLoading}}
        maskClosable={!importLoading}
        closable={!importLoading}
        onOk={() => {
          const fromGitBranch = importFromGitBranch.trim() || 'master'
          setImportLoading(true);
          providersConfig.importModels({fromGitBranch: fromGitBranch, toBranch: branch})
            .then((resp) => {
              setImportAskOpen(false);
              setImportRes(resp.res);
              setImportOpen(true)
              loadModels();
            })
            .catch((e: any) => {
              ErrorView(modal, 'importModels error', e)
            })
            .finally(() => setImportLoading(false))
        }}
        okText="Import"
      >
        <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
          <span style={{fontWeight: 500}}>from git branch</span>
          <Input
            placeholder="master"
            value={importFromGitBranch}
            onChange={(e) => setImportFromGitBranch(e.target.value)}
          />
          <span style={{fontWeight: 500}}>to branch</span>
          <span>{branch}</span>
        </div>
      </Modal>
      <ResultModal
        title={`Import from git — ${branch}`}
        open={importOpen}
        res={importRes}
        onClose={() => {
          setImportOpen(false);
          loadModels()
        }}
      />
      <ResultModal
        title={`Export to git — ${branch}`}
        open={exportOpen}
        res={exportRes}
        onClose={() => setExportOpen(false)}
      />
      {contextHolder}
    </>
  )
}